import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  BookOpen, Quote, Library, ScrollText, Tags, BookText, Link as LinkIcon, Loader2, ListTree,
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Verse Lens — unified side panel that shows a single verse's coverage
 * across every commentary / cross-reference / topical / encyclopedia source
 * in one tap.
 *
 * Open by setting `open=true` + passing `book/chapter/verse`. The panel
 * fetches `/api/resources/verse-lens` once per (verse) and renders six
 * cards: Matthew Henry, JFB, Adam Clarke, TSK cross-refs, Nave's topics,
 * ISBE encyclopedia. Each card has a CTA that emits the existing
 * `lumera:verse-select` event (for TSK refs) or navigates to the
 * dedicated pane / sheet (so users can drill deeper).
 *
 * Anyone in the codebase can open the lens by dispatching
 *   window.dispatchEvent(new CustomEvent('lumera:verse-lens',
 *     { detail: { book, chapter, verse } }))
 * which the host component listens for. We avoid prop drilling that way.
 */

const SectionCard = ({ icon: Icon, title, accent, children, footer, testid }) => (
  <div
    className={`rounded-xl border-2 ${accent} p-4 space-y-2`}
    data-testid={testid}
  >
    <div className="flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-slate-100">
      <Icon className="w-4 h-4" />
      {title}
    </div>
    <div className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
      {children}
    </div>
    {footer && <div className="pt-1 text-[10px] uppercase tracking-widest text-slate-500">{footer}</div>}
  </div>
);

const EmptyHint = ({ children }) => (
  <p className="text-xs italic text-slate-500">{children}</p>
);

const VerseLensPanel = ({ open, onOpenChange, book, chapter, verse }) => {
  const { language } = useLanguage();
  const isES = language === 'es';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !book || !chapter || !verse) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${BACKEND_URL}/api/resources/verse-lens`, {
      params: { book, chapter, verse },
    })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setError(isES ? 'Error al cargar' : 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, book, chapter, verse, isES]);

  // Lazy-load the AI outline ONLY on demand (button click) so the lens
  // opens fast — outline generation costs an LLM call.
  const [outline, setOutline] = useState(null);
  const [outlineLoading, setOutlineLoading] = useState(false);
  useEffect(() => { setOutline(null); }, [book, chapter, verse]);
  const loadOutline = async () => {
    if (outline || outlineLoading) return;
    setOutlineLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/ai/outline`, {
        params: { book, chapter },
        withCredentials: true,
      });
      setOutline(r.data);
    } catch {
      setOutline({ error: true });
    } finally {
      setOutlineLoading(false);
    }
  };

  const handleTskClick = (r) => {
    const target = `${r.to_book} ${r.to_chapter}:${r.to_verse}${r.to_verse_end ? `-${r.to_verse_end}` : ''}`;
    window.dispatchEvent(new CustomEvent('lumera:verse-select', { detail: { reference: target } }));
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-hidden flex flex-col p-0"
        data-testid="verse-lens-sheet"
      >
        <SheetHeader className="px-5 pt-5 pb-3 border-b bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/40 dark:to-purple-950/40">
          <SheetTitle className="flex items-center gap-2 text-indigo-900 dark:text-indigo-100">
            <BookOpen className="w-5 h-5" />
            {isES ? 'Lente del Versículo' : 'Verse Lens'}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {book} {chapter}:{verse}
          </SheetDescription>
          {data?.available_count != null && (
            <div className="pt-1">
              <Badge variant="secondary" className="text-[10px]">
                {data.available_count}/7 {isES ? 'fuentes' : 'sources'}
              </Badge>
            </div>
          )}
          {data?.verse?.text && (
            <p className="text-sm italic text-slate-700 dark:text-slate-300 leading-relaxed pt-1">
              “{data.verse.text}”
            </p>
          )}
        </SheetHeader>
        <ScrollArea className="flex-1 px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
            </div>
          )}
          {error && !loading && (
            <p className="text-sm text-red-600 italic">{error}</p>
          )}
          {data && !loading && (
            <div className="space-y-3" data-testid="verse-lens-body">
              {/* Matthew Henry */}
              <SectionCard
                icon={Library}
                title={isES ? 'Matthew Henry' : 'Matthew Henry'}
                accent="border-emerald-300/60 bg-emerald-50/40 dark:border-emerald-800/40 dark:bg-emerald-950/20"
                footer={data.henry?.source}
                testid="lens-card-henry"
              >
                {data.henry ? (
                  <>
                    {data.henry.topic && (
                      <p className="text-xs uppercase tracking-widest text-emerald-700 dark:text-emerald-300 mb-1">
                        v. {data.henry.verses} · {data.henry.topic}
                      </p>
                    )}
                    <p>{data.henry.snippet}</p>
                  </>
                ) : (
                  <EmptyHint>{isES ? 'Sin entrada para este versículo' : 'No entry for this verse'}</EmptyHint>
                )}
              </SectionCard>

              {/* JFB */}
              <SectionCard
                icon={Library}
                title="Jamieson-Fausset-Brown"
                accent="border-amber-300/60 bg-amber-50/40 dark:border-amber-800/40 dark:bg-amber-950/20"
                footer="JFB · Public Domain (1871)"
                testid="lens-card-jfb"
              >
                {data.jfb ? <p>{data.jfb.snippet}</p> : <EmptyHint>{isES ? 'Sin entrada' : 'No entry'}</EmptyHint>}
              </SectionCard>

              {/* Adam Clarke */}
              <SectionCard
                icon={Quote}
                title="Adam Clarke"
                accent="border-sky-300/60 bg-sky-50/40 dark:border-sky-800/40 dark:bg-sky-950/20"
                footer="Clarke · Public Domain (1810-1825)"
                testid="lens-card-clarke"
              >
                {data.clarke ? <p>{data.clarke.snippet}</p> : <EmptyHint>{isES ? 'Sin entrada' : 'No entry'}</EmptyHint>}
              </SectionCard>

              {/* John Gill */}
              <SectionCard
                icon={ScrollText}
                title="John Gill"
                accent="border-teal-300/60 bg-teal-50/40 dark:border-teal-800/40 dark:bg-teal-950/20"
                footer="Gill's Exposition · Public Domain (1746-1763)"
                testid="lens-card-gill"
              >
                {data.gill ? (
                  <p>{data.gill.snippet}</p>
                ) : (
                  <EmptyHint>
                    {isES ? 'Sin entrada (corpus en progreso)' : 'No entry yet (corpus seeding in progress)'}
                  </EmptyHint>
                )}
              </SectionCard>

              {/* AI Passage Outline — Logos-parity Phase 1. Lazy-loaded
                  via /api/ai/outline. Grounds itself in Matthew Henry /
                  JFB / Clarke commentary already in our DB. */}
              <SectionCard
                icon={ListTree}
                title={isES ? 'Bosquejo del Pasaje (IA)' : 'Passage Outline (AI)'}
                accent="border-fuchsia-300/60 bg-fuchsia-50/40 dark:border-fuchsia-800/40 dark:bg-fuchsia-950/20"
                footer={isES ? 'Generado por IA · basado en comentarios públicos' : 'AI-generated · grounded in public-domain commentary'}
                testid="lens-card-outline"
              >
                {!outline && !outlineLoading && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={loadOutline}
                    data-testid="lens-outline-load-btn"
                    className="h-7 text-xs border-fuchsia-300 text-fuchsia-700 dark:text-fuchsia-300"
                  >
                    {isES ? `Generar bosquejo de ${book} ${chapter}` : `Generate outline for ${book} ${chapter}`}
                  </Button>
                )}
                {outlineLoading && (
                  <div className="flex items-center gap-2 text-fuchsia-700 dark:text-fuchsia-300 text-xs">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {isES ? 'Generando…' : 'Generating…'}
                  </div>
                )}
                {outline?.error && (
                  <EmptyHint>{isES ? 'Bosquejo no disponible' : 'Outline unavailable'}</EmptyHint>
                )}
                {outline?.points?.length > 0 && (
                  <ol className="space-y-1.5 list-decimal list-inside text-xs">
                    {outline.points.map((p, i) => (
                      <li key={i} className="leading-snug" data-testid={`lens-outline-point-${i}`}>
                        <span className="font-semibold text-fuchsia-700 dark:text-fuchsia-300">{p.title}</span>
                        {p.verses && <span className="text-slate-500 ml-1.5">· {p.verses}</span>}
                        {p.summary && <div className="text-slate-600 dark:text-slate-400 italic ml-5">{p.summary}</div>}
                      </li>
                    ))}
                  </ol>
                )}
              </SectionCard>

              {/* TSK Cross-References */}
              <SectionCard
                icon={LinkIcon}
                title={isES ? 'Referencias Cruzadas (TSK)' : 'Cross-References (TSK)'}
                accent="border-purple-300/60 bg-purple-50/40 dark:border-purple-800/40 dark:bg-purple-950/20"
                footer="openbible.info · CC-BY 4.0"
                testid="lens-card-tsk"
              >
                {data.tsk?.length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {data.tsk.map((r, idx) => {
                      const label = `${r.to_book} ${r.to_chapter}:${r.to_verse}${r.to_verse_end ? `-${r.to_verse_end}` : ''}`;
                      return (
                        <Button
                          key={`${r.to_book}-${r.to_chapter}-${r.to_verse}-${idx}`}
                          size="sm"
                          variant="outline"
                          onClick={() => handleTskClick(r)}
                          className="h-7 text-xs"
                          title={`${r.votes} community votes`}
                          data-testid={`lens-tsk-chip-${idx}`}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyHint>{isES ? 'Sin referencias cruzadas' : 'No cross-references'}</EmptyHint>
                )}
              </SectionCard>

              {/* Nave's Topical */}
              <SectionCard
                icon={Tags}
                title={isES ? 'Biblia Temática (Nave)' : "Nave's Topical Bible"}
                accent="border-rose-300/60 bg-rose-50/40 dark:border-rose-800/40 dark:bg-rose-950/20"
                testid="lens-card-naves"
              >
                {data.naves?.length ? (
                  <div className="flex flex-wrap gap-2">
                    {data.naves.map((t) => (
                      <Badge
                        key={t.slug}
                        variant="secondary"
                        className="cursor-pointer hover:bg-rose-200 dark:hover:bg-rose-900"
                        data-testid={`lens-naves-${t.slug}`}
                      >
                        {t.topic}
                        <span className="ml-1 text-[10px] opacity-60">({t.verse_count})</span>
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <EmptyHint>{isES ? 'Este versículo no está indexado por Nave' : 'No matching topic'}</EmptyHint>
                )}
              </SectionCard>

              {/* ISBE Encyclopedia */}
              <SectionCard
                icon={BookText}
                title={isES ? 'Enciclopedia (ISBE)' : 'Bible Encyclopedia (ISBE)'}
                accent="border-indigo-300/60 bg-indigo-50/40 dark:border-indigo-800/40 dark:bg-indigo-950/20"
                footer="ISBE 1915 · Public Domain"
                testid="lens-card-isbe"
              >
                {data.isbe?.length ? (
                  <div className="space-y-2">
                    {data.isbe.map((e) => (
                      <div key={e.slug} data-testid={`lens-isbe-${e.slug}`} className="border-l-2 border-indigo-400 pl-2">
                        <div className="flex items-baseline gap-2 mb-0.5">
                          <span className="text-xs uppercase tracking-widest text-indigo-600 dark:text-indigo-300">
                            {e.matched_word}
                          </span>
                          <span className="font-semibold text-sm">{e.title}</span>
                        </div>
                        <p className="text-xs leading-relaxed">{e.snippet}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyHint>{isES ? 'Sin nombres propios encontrados' : 'No proper-noun matches'}</EmptyHint>
                )}
              </SectionCard>

              <p className="text-[10px] uppercase tracking-widest text-slate-500 text-center pt-4">
                {isES ? 'Verse Lens · siete fuentes, un solo toque' : 'Verse Lens · seven sources, one tap'}
              </p>
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

/**
 * Host wrapper — mount once at the top of the app. Listens for the
 * `lumera:verse-lens` custom event and opens the panel. This keeps every
 * verse-rendering component free of prop drilling.
 */
export const VerseLensHost = () => {
  const [target, setTarget] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      const { book, chapter, verse } = e?.detail || {};
      if (!book || !chapter || !verse) return;
      setTarget({ book, chapter, verse });
      setOpen(true);
    };
    window.addEventListener('lumera:verse-lens', handler);
    return () => window.removeEventListener('lumera:verse-lens', handler);
  }, []);

  return (
    <VerseLensPanel
      open={open}
      onOpenChange={setOpen}
      book={target?.book}
      chapter={target?.chapter}
      verse={target?.verse}
    />
  );
};

export default VerseLensPanel;
